<script setup lang="ts">
import Auth from '@/api/Auth'
import Country from '@/api/Country'
import { useValidator } from '@/composables/validator'
import useAuthStore from '@/stores/auth'
import { AppBlock, AppButton, useDialog, useToast } from 'lilasia-ui'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { z } from 'zod'
import CloseButton from '../CloseButton.vue'
import CountrySelect from '../CountrySelect.vue'
import PhoneNumberInput from '../PhoneNumberInput.vue'

const { hideDialog } = useDialog()
const { toastSuccess } = useToast()
const { t } = useI18n()

const authStore = useAuthStore()

const { user } = storeToRefs(authStore)
const countries = ref<Country[]>([])
const selectedCountry = ref<Country | null>(null)
const loading = ref(false)

const schema = z.object({
  dial_code: z.string(),
  phone_number: z.string(),
  country_id: z.number()
})

const {
  formData: form,
  formError: error,
  validate
} = useValidator(schema, {
  dial_code: user.value.dial_code || '+65',
  phone_number: user.value.phone_number || '',
  country_id: user.value.country_id || 0
})

const submitDisabled = computed(() => {
  return !form.value.dial_code || !form.value.phone_number || !form.value.country_id
})

onMounted(async () => {
  await fetchCountries()

  selectedCountry.value =
    countries.value.find((country) => country.id === authStore.user.country_id) ?? null
})

const fetchCountries = async () => {
  try {
    countries.value = await Country.get<Country[]>()
  } catch (error) {
    const fetchError = error as FetchError
    console.error(fetchError.message)
  }
}

const fetchProfile = async () => {
  try {
    authStore.user = await Auth.getUserProfile<User>()
  } catch (error) {
    const fetchError = error as FetchError
    console.error(fetchError.message)
  }
}

const handleCountryChange = (country: Country | null) => {
  if (country?.id) {
    form.value.country_id = country?.id
  }
}

const submit = async () => {
  if (!validate()) {
    return
  }

  try {
    const { message } = await Auth.updateUserProfile<APIResponse>({
      name: user.value.name,
      country_id: form.value.country_id,
      phone_number: form.value.phone_number,
      dial_code: form.value.dial_code
    })

    toastSuccess(message)
    fetchProfile()
    hideDialog()
  } catch (error) {
    const fetchError = error as FetchError
    console.error(fetchError.message)
  }
}
</script>

<template>
  <form
    class="w-full md:w-[600px]"
    @submit.prevent="submit"
  >
    <AppBlock>
      <template #header>
        <div class="flex items-center justify-between">
          <div class="text-20 font-600">Complete your profile</div>
          <CloseButton @click="hideDialog" />
        </div>
      </template>

      <div class="space-y-16 px-24 pb-16">
        <div class="text-14 leading-20 text-black-60">
          We just need a few more details. Please enter your phone number and select your country.
        </div>

        <PhoneNumberInput
          id="phone-number"
          v-model:dial-code="form.dial_code"
          v-model:phone-number="form.phone_number"
          :label="t('phone_number')"
          :countries="countries"
          :error="error.phone_number"
          required
        />

        <CountrySelect
          id="country"
          v-model="selectedCountry"
          :label="t('country')"
          :countries="countries"
          :error="error.country_id"
          required
          @update:model-value="handleCountryChange"
        />
      </div>

      <template #footer>
        <div class="flex justify-end">
          <AppButton
            type="submit"
            color="blue"
            :text="t('save')"
            :loading="loading"
            :disabled="submitDisabled"
          />
        </div>
      </template>
    </AppBlock>
  </form>
</template>
